import { SectionHeader } from "./SectionHeader";

const Card = ({ title, desc, children, testid, className = "" }) => (
    <div
        data-testid={testid}
        className={`group flex flex-col border border-[#262626] bg-[#050505] p-6 transition-colors duration-200 hover:border-green-400/40 hover:bg-[#0a0a0a] sm:p-7 ${className}`}
    >
        <h3 className="text-lg font-medium text-neutral-100">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-400">{desc}</p>
        {children && <div className="mt-5 flex-1">{children}</div>}
    </div>
);

const Chip = ({ children, tone = "default" }) => (
    <span
        className={`inline-block border px-2 py-1 font-mono text-[10px] tracking-wider ${
            tone === "green"
                ? "border-green-400/40 text-green-400"
                : tone === "amber"
                  ? "border-amber-400/40 text-amber-400"
                  : "border-[#262626] text-neutral-400"
        }`}
    >
        {children}
    </span>
);

const BARS = [38, 55, 42, 70, 62, 84, 58, 92, 66, 78, 88, 95];

const Features = () => (
    <section id="features" data-testid="features-section" className="border-t border-[#262626]">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
            <SectionHeader id="features">Reuse The Runs That Worked</SectionHeader>
            <div className="grid gap-px border border-[#262626] bg-[#262626] sm:grid-cols-2 lg:grid-cols-3">
                <Card
                    testid="feature-recall-latency"
                    title="Recall in ~60ms"
                    desc="Three matchers at once: exact, lexical, semantic."
                >
                    <div className="flex items-end justify-between gap-4">
                        <span className="font-mono text-4xl font-semibold text-green-400">60ms</span>
                        <div className="flex gap-1.5">
                            <Chip tone="green">EXACT</Chip>
                            <Chip>LEXICAL</Chip>
                            <Chip>SEMANTIC</Chip>
                        </div>
                    </div>
                </Card>
                <Card
                    testid="feature-what-is-stored"
                    title="What is stored"
                    desc="The files touched, the command that proved it worked, and the order — with real exit codes. The transcript is never sent."
                >
                    <div className="border border-[#262626] bg-black p-4 font-mono text-[11px] leading-5">
                        <div className="text-neutral-500">procedure_a91.json</div>
                        <div className="mt-2 text-neutral-400">
                            files touched <span className="text-green-400">gateway.py</span>
                        </div>
                        <div className="text-neutral-400">
                            verify <span className="text-green-400">pytest test.py</span>
                        </div>
                        <div className="text-neutral-400">
                            exit code <span className="text-green-400">0</span>
                        </div>
                        <div className="text-neutral-400">
                            order <span className="text-green-400">1 → 2 → 3</span>
                        </div>
                    </div>
                </Card>
                <Card
                    testid="feature-works-with-agent"
                    title="Works with your agent"
                    desc="Claude Code, Codex, Cursor and OpenCode have curated tool registries. Any other harness sends one JSON trace."
                >
                    <div className="flex flex-wrap gap-1.5">
                        <Chip tone="green">Claude Code</Chip>
                        <Chip>Codex</Chip>
                        <Chip>Cursor</Chip>
                        <Chip>OpenCode</Chip>
                        <Chip tone="amber">Any harness</Chip>
                    </div>
                </Card>
                <Card
                    testid="feature-chained-plans"
                    title="Chained plans"
                    desc="When a task is several things, recall returns an ordered plan of procedures and names any gap."
                >
                    <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px]">
                        <Chip>issue_refund</Chip>
                        <span className="text-neutral-600">→</span>
                        <Chip>customer-history</Chip>
                        <span className="text-neutral-600">→</span>
                        <Chip>escalation-policy</Chip>
                        <span className="text-neutral-600">→</span>
                        <Chip tone="green">close_ticket</Chip>
                    </div>
                </Card>
                <Card
                    testid="feature-savings"
                    title="Savings per run"
                    desc="Tokens, money and time, next to the run they came from — including the runs where memory did not help."
                >
                    <div className="flex h-20 items-end gap-1.5">
                        {BARS.map((h, i) => (
                            <div
                                key={i}
                                className="flex-1 bg-green-400/25 transition-colors group-hover:bg-green-400/50"
                                style={{ height: `${h}%` }}
                            />
                        ))}
                    </div>
                    <div className="mt-2 font-mono text-[10px] tracking-[0.2em] text-neutral-600">
                        TIME SAVED vs. COLD RUNS · R1–R12
                    </div>
                </Card>
                <Card
                    testid="feature-scrubbed-identifiers"
                    title="Scrubbed identifiers"
                    desc="Only the prompt and allow-listed tool arguments leave the machine."
                >
                    <div className="grid grid-cols-2 gap-px border border-[#262626] bg-[#262626] font-mono text-[10px]">
                        <div className="bg-black p-3">
                            <div className="mb-2 tracking-[0.2em] text-neutral-600">STAYS ON THIS MACHINE</div>
                            <div className="text-neutral-500 line-through">session_id</div>
                            <div className="text-neutral-500 line-through">user_email</div>
                            <div className="text-neutral-500 line-through">cwd</div>
                            <div className="text-neutral-500 line-through">raw tool args</div>
                        </div>
                        <div className="bg-black p-3">
                            <div className="mb-2 tracking-[0.2em] text-green-400">ONLY THIS LEAVES</div>
                            <div className="text-neutral-300">prompt</div>
                            <div className="text-neutral-300">command</div>
                            <div className="text-neutral-300">file_path</div>
                        </div>
                    </div>
                </Card>
                <Card
                    testid="feature-tool-change"
                    title="Survives a tool changing shape"
                    desc="Procedures name a target by what it is about, not by field names — so a renamed tool still matches."
                    className="sm:col-span-2 lg:col-span-3"
                >
                    <div className="border border-[#262626] bg-black p-4 font-mono text-[11px] leading-6">
                        <div>
                            <span className="text-green-400">$</span>{" "}
                            <span className="text-neutral-100">remembered recall "process a customer refund"</span>
                            <span className="text-neutral-600">  # matched on shared tokens, not field names</span>
                        </div>
                        <div className="text-amber-400">
                            0.86 procedures/9f21ac4d-process-a-customer-refund [lexical]
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    </section>
);

export default Features;
